import { FC, useCallback, useEffect, useRef } from 'react';
import { useSetAtom } from 'jotai';
import SubscriptionGuideModal, { SUBSCRIPTION_GUIDE_LAND_FROM } from 'src/components/AzarOnly/Subscription/SubscriptionGuideModal';
import { RootStyle } from 'src/components/Payments/style';
import EmptyProducts from 'src/components/Shop/EmptyProducts';
import useLogin from 'src/hooks/useLogin';
import { showModalAtom } from 'src/stores/modal/atoms';
import { EVENT_NAME, EVENT_TYPE } from 'src/types/Event';
import { ModalType } from 'src/types/Modal';
import { pushEvent } from 'src/utils/event-gateway';
import { setTargetBlankToLinks } from 'src/utils/html';
import GemProductList from './GemProductList';
import ShopTabs from './ShopTabs';
import { ShopContainer, ShopScrollContent, TopDim, BottomDim, DimTrigger, StickyTabsWrapper, LegalSection, LegalList, LegalItem } from './styles';
import { SubscriptionProduct } from './SubscriptionCard';
import SubscriptionList from './SubscriptionList';
import useScrollEdge from './useScrollEdge';
import useShopData from './useShopData';
import useShopTabs from './useShopTabs';
import WebExclusiveBanner from './WebExclusiveBanner';
const ShopContent: FC = () => {
  const showModal = useSetAtom(showModalAtom);
  const {
    banner,
    gems,
    subscriptions,
    footer,
    tabs
  } = useShopData();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const {
    visibleTabs,
    activeTab,
    scrollToTab,
    setSectionRef
  } = useShopTabs({
    scrollContainerRef,
    gems,
    subscriptions,
    tabs
  });
  const {
    topTriggerRef,
    bottomTriggerRef,
    isTopReached,
    isBottomReached
  } = useScrollEdge({
    scrollContainerRef
  });
  const handleSubscribe = useCallback((sub: SubscriptionProduct) => {
    showModal({
      key: ModalType.SUBSCRIPTION_GUIDE,
      component: () => <SubscriptionGuideModal defaultPageId={sub.categoryId} landFrom={SUBSCRIPTION_GUIDE_LAND_FROM.SHOP} />
    });
  }, [showModal]);
  if (!gems && !subscriptions) {
    return <ShopContainer>
        <EmptyProducts />
      </ShopContainer>;
  }
  return <ShopContainer data-sentry-element="ShopContainer" data-sentry-component="ShopContent" data-sentry-source-file="Shop.tsx">
      <ShopScrollContent ref={scrollContainerRef} data-sentry-element="ShopScrollContent" data-sentry-source-file="Shop.tsx">
        {banner && <WebExclusiveBanner title={banner.title} iconUrl={banner.iconUrl} modules={banner.modules} />}
        <DimTrigger ref={topTriggerRef} data-sentry-element="DimTrigger" data-sentry-source-file="Shop.tsx" />
        {visibleTabs.length > 0 && <StickyTabsWrapper>
            <ShopTabs tabs={visibleTabs} activeTab={activeTab} onTabChange={scrollToTab} />
          </StickyTabsWrapper>}
        <TopDim $isShow={!isTopReached} $hasTabs={visibleTabs.length > 0} data-sentry-element="TopDim" data-sentry-source-file="Shop.tsx" />
        {gems && <div ref={gems.anchor ? setSectionRef(gems.anchor) : undefined}>
            <GemProductList gemProducts={gems.products} sectionTitle={gems.title} />
          </div>}
        {subscriptions && <div ref={subscriptions.anchor ? setSectionRef(subscriptions.anchor) : undefined}>
            <SubscriptionList subscriptions={subscriptions.products} sectionTitle={subscriptions.title} onSubscribe={handleSubscribe} />
          </div>}
        {footer && <LegalSection>
            <LegalList>
              {footer.items.map(item => <LegalItem key={item.moduleId} dangerouslySetInnerHTML={{
            __html: setTargetBlankToLinks(item.text)
          }} />)}
            </LegalList>
          </LegalSection>}
        <DimTrigger ref={bottomTriggerRef} data-sentry-element="DimTrigger" data-sentry-source-file="Shop.tsx" />
        <BottomDim $isShow={!isBottomReached} data-sentry-element="BottomDim" data-sentry-source-file="Shop.tsx" />
      </ShopScrollContent>
    </ShopContainer>;
};
const Shop: FC = () => {
  const {
    isLogin
  } = useLogin();
  useEffect(() => {
    if (isLogin) {
      pushEvent({
        eventType: EVENT_TYPE.PURCHASE,
        eventName: EVENT_NAME.SHOP_OPEN
      });
    }
  }, [isLogin]);
  if (!isLogin) {
    return <RootStyle />;
  }
  return <ShopContent data-sentry-element="ShopContent" data-sentry-component="Shop" data-sentry-source-file="Shop.tsx" />;
};
export default Shop;